// Read-back voice for the tutor: tries premium Google TTS (Pro/Teams), falls
// back to browser SpeechSynthesis on any failure (not configured / offline).
// Exposes a single speak/stop/isSpeaking surface so the UI doesn't care which
// provider answered.

import { useCallback, useEffect, useRef, useState } from 'react';
import { synthesizeSpeech } from '@shared/services/voice';
import { useTextToSpeech } from './useTextToSpeech';

export interface ReplyVoice {
  speak: (text: string) => void;
  stop: () => void;
  isSpeaking: boolean;
}

export function useReplyVoice(): ReplyVoice {
  const browser = useTextToSpeech();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const reqIdRef = useRef(0);
  const [premiumSpeaking, setPremiumSpeaking] = useState(false);

  const stop = useCallback(() => {
    reqIdRef.current += 1; // invalidate any in-flight synthesis
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setPremiumSpeaking(false);
    browser.stop();
  }, [browser]);

  const speak = useCallback(
    (text: string) => {
      stop();
      const myReq = reqIdRef.current;
      void (async () => {
        try {
          const { audioContent, mimeType } = await synthesizeSpeech(text);
          if (myReq !== reqIdRef.current) return; // superseded/stopped
          const audio = new Audio(`data:${mimeType};base64,${audioContent}`);
          audioRef.current = audio;
          setPremiumSpeaking(true);
          audio.onended = () => {
            if (audioRef.current === audio) audioRef.current = null;
            setPremiumSpeaking(false);
          };
          audio.onerror = () => {
            if (audioRef.current === audio) audioRef.current = null;
            setPremiumSpeaking(false);
            void browser.speak(text).catch(() => {});
          };
          await audio.play();
        } catch {
          // Not entitled / not configured / network — fall back to browser TTS.
          if (myReq === reqIdRef.current) void browser.speak(text).catch(() => {});
        }
      })();
    },
    [browser, stop],
  );

  useEffect(() => () => stop(), [stop]);

  return { speak, stop, isSpeaking: premiumSpeaking || browser.isSpeaking };
}
